import { useCallback, useState } from 'react'
import { IDeleteOutboundFilesReport, IUploadOutboundFilesReport } from 'types/graphqlTypes'
import { useListShipmentPendingFilesLazyQuery } from '../graphql/ListShipmentPendingFiles'
import { useDeleteOutboundFiles } from './useStagingTestingDeleteOutboundFiles'
import { useUploadOutboundFiles } from './useStagingTestingUploadOutboundFiles'

export const useStagingTestingFiles = () => {
  const [
    refetch,
    { data: fileListQuery, error: fileListingError, loading: fileListingLoading },
  ] = useListShipmentPendingFilesLazyQuery({
    fetchPolicy: 'network-only',
  })

  const { shipmentPendingOutboundRawEdiFiles: pendingFiles = [] } = fileListQuery || {}

  const [uploadFilesReport, setUploadFilesReport] = useState<IUploadOutboundFilesReport>()
  const [deleteFilesReport, setDeleteFilesReport] = useState<IDeleteOutboundFilesReport>()

  const clearReports = () => {
    setUploadFilesReport(undefined)
    setDeleteFilesReport(undefined)
  }

  const loadFiles = (shipmentId: string) => {
    clearReports()
    refetch({ variables: { shipmentId } })
  }

  const { loading: uploadLoading, uploadOutboundFiles } = useUploadOutboundFiles()
  const { deleteOutboundFiles, loading: deleteLoading } = useDeleteOutboundFiles()

  const uploadFiles = useCallback(
    async (outboundRawEdiFileIds: string[], outboundConnectionConfigId, shipmentId: string) => {
      clearReports()
      const report = await uploadOutboundFiles({
        outboundRawEdiFileIds,
        outboundConnectionConfigId,
      })
      if (report) {
        setUploadFilesReport(report)
        if (report.success) {
          loadFiles(shipmentId)
        }
      }
    },
    []
  )

  const deleteFiles = useCallback(async (outboundRawEdiFileIds: string[], shipmentId: string) => {
    clearReports()
    const report = await deleteOutboundFiles({ outboundRawEdiFileIds })
    if (report) {
      setDeleteFilesReport(report)
      if (report.success) {
        loadFiles(shipmentId)
      }
    }
  }, [])

  const errorMessage =
    uploadFilesReport?.errorMessage || deleteFilesReport?.errorMessage || fileListingError?.message
  const errorBacktrace =
    uploadFilesReport?.errorBacktrace || deleteFilesReport?.errorBacktrace || undefined
  const loading = uploadLoading || deleteLoading || fileListingLoading

  return {
    loadFiles,
    pendingFiles,
    uploadFiles,
    deleteFiles,
    loading,
    errorMessage,
    errorBacktrace,
  }
}
